import React, { useState, useEffect,  } from 'react';
import { withPrefix } from 'gatsby';
import { css } from 'styled-components';
import Button from '@vfuk/core-button';
import IconButton from '@vfuk/core-icon-button';
import runSeoEventData from '../../../../helpers/runSeoData';
import uniqueId from '../../../../helpers/uniqueId';
import { ThemeProvider } from 'styled-components';
import themeWS10 from '@vfuk/core-theme-ws10';
import SimpleGrid from '@vfuk/core-simple-grid';
import { Pill } from '@vfuk/core-pill';
import {
  SimpleGridWrapper,
  gridContainer,
  buttonWithoutIconCSS,
  pillBetaCSS,
  pillBetaTooltipCSS,
  buttonWithIconCSS,
  buttonAlignmentLeft,
  buttonAlignmentRight,
  buttonMarginLeft,
} from './styles/CtaButtonRow.styles';
import { CtaButtonProps, CtaButtonRowProps } from './types/CtaButtonRow.types';
import fireButtonAndLinkClicksEvent from '../../../../helpers/fireButtonAndLinkClicksEvent';
import { addHashIfMissing } from '../../../../helpers/navigationHelpers';

const MOBILE_VIEW_WIDTH = 638;

const displayChat = (showChat: boolean) => {
  const chatContainer = document.querySelector('#be-chat-container');
  if (chatContainer && showChat === true) {
    brandembassy('openChatWindow');
    brandembassy(
      'setCustomCss',
      "[data-selector='MESSENGER'] {display: block}"
    );
  }
  // Issue with brandembassy 'closeWindow' method
  // NOTE: Toggle the chat window to close
  // if (chatContainer && showChat === false) {
  // NOTE: The below brandembassy method 'Close the window' does not work
  // brandembassy('closeWindow');
  // }
};

const customTooltipColors = (
  tooltipBgColor = '#333333',
  tooltipLableColor = '#fffff'
) => {
  return css`
    span {
      &:before {
        border-right: 15px solid ${tooltipBgColor}!important;
      }
      &:after {
        color: ${tooltipLableColor}!important;
        background-color: ${tooltipBgColor}!important;
      }
    }
  `;
};

const getColNums = (buttons: CtaButtonProps[]) => {
  switch (buttons.length) {
    case 2:
      return 3;
      break;
    default:
      return 4;
  }
};

const CtaButtonRow: React.FC<CtaButtonRowProps> = ({
  buttonRowAlignment,
  isMobileViewButtonOrderReversed,
  buttons,
  spacing = 12
}) => {
  const [showChat, setShowChat] = useState(true);
  const [pageTitle, setPageTitle] = useState('');
  const isMobile = checkWindowSize();

  useEffect(() => {
    setPageTitle(document !== undefined ? document.title : '');
  }, []);
  function checkWindowSize() {
    const [isMobile, setIsMobile] = useState(false);
    if (typeof window !== 'undefined') {
      useEffect(() => {
        function handleResize() {
          if (window.innerWidth <= MOBILE_VIEW_WIDTH) {
            setIsMobile(true);
          }
        }
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
      }, []);
    }
    return isMobile;
  }

  if (buttons && buttons.length > 0) {
    const orderedButtons = isMobile && isMobileViewButtonOrderReversed ? buttons.slice().reverse() : buttons;
    const renderedButtons = orderedButtons.map((button, id) => {
      let appearance = button.rank;
      let buttonText = button.label;
      let buttonInverse = button.inverse;

      if (
        isMobile &&
        button.mobileLabel &&
        button.mobileLabel !== 'no_content'
      ) {
        buttonText = button.mobileLabel
      }

      if (
        isMobile &&
        button.mobileViewRank &&
        String(button.mobileViewRank) !== 'no_content'
      ) {
        appearance = button.mobileViewRank;
      } else if (
        !button.rank ||
        button.rank === 'none' ||
        button.rank === 'transparent'
      ) {
        appearance = 'primary';
      } else if (button.rank && button.rank === 'tertiary') {
        appearance = 'secondary';
      }

      if (isMobile) {
        buttonInverse = button.mobileViewInverse;
      } else if (button.rank && button.rank === 'tertiary') {
        buttonInverse = true;
      }

      const buttonCSS = [];
      const pillCSS = []
      const getTooltipTitle =
        button.iconRef === 'beta_tooltip' && button.tooltipLabel !== ''
          ? button.tooltipLabel
          : '';

      const dataAttributes = {
        'data-title': getTooltipTitle,
        'data-analytics-element': uniqueId(buttonText),
        'data-analytics-context': pageTitle,
        'data-analytics-category': button.linkType,
        title: buttonText,
      };

      const customAttributeData = (button.customAttributeData && button.customAttributeData !== "{}")
        ? JSON.parse(button.customAttributeData.customAttributeData)
        : {};

      const htmlAttributes = {
        type: 'button',
        dataAttributes: { ...dataAttributes },
        target: customAttributeData?.target
      };

      const getButtonHref = (): string | undefined => {
        if (button.linkType === 'chat') {
          return undefined;
        }

        if (button.linkType === 'scroll') {
          return addHashIfMissing(button.scrollToId || '');
        }

        return withPrefix(button.url);
      };


      const onButtonClick = () => {
        if (button.seoEventData) {
          runSeoEventData(button.seoEventData.seoEventData);
        }
        fireButtonAndLinkClicksEvent({
          event_label: buttonText,
          event_action: 'button click',
        });

        if (button.linkType === 'chat') {
          setShowChat(!showChat);
          displayChat(showChat);
        }
      }

      // if the iconRef contentful field is empty, it returns the string 'no_content'
      if (button.iconRef === null || button.iconRef === 'no_content') {
        buttonCSS.push(buttonWithoutIconCSS);

        return (
          <div css={buttonCSS} className={`button ${button.rank === "tertiary" ? "button--tertiary-wrapper" : ""}`} key={button.id}>
            <Button
              className="ctaButton"
              href={getButtonHref()}
              dataAttributes={{ ...dataAttributes }}
              htmlAttributes={{ ...htmlAttributes }}
              {...customAttributeData}
              text={buttonText}
              appearance={appearance}
              inverse={buttonInverse}
              onClick={onButtonClick}
            />
          </div>
        );
      } else {
        buttonCSS.push(buttonWithIconCSS);

        if (id > 0) {
          buttonCSS.push(buttonMarginLeft);
        }

        if (button.iconRef === 'beta_tooltip') {
          if (button.tooltipLabel === '') {
            pillCSS.push(pillBetaCSS);
          } else {
            pillCSS.push(pillBetaCSS, pillBetaTooltipCSS);
            if (
              button.tooltipLabelColor !== '' &&
              button.tooltipBackgroundColor !== ''
            ) {
              const getCustomTooltipcolors = customTooltipColors(
                button.tooltipBackgroundColor,
                button.tooltipLabelColor
              );
              pillCSS.push(getCustomTooltipcolors);
            }
          }
          if (button.rank === 'none') {
            appearance = 'primary';
          }

          const dataAttributes = {
            'data-title': getTooltipTitle || ''
          }

          return (
            <div css={pillCSS} key={button.id}>
              <Pill
                text={button.label}
                appearance={'primary'}
                dataAttributes={dataAttributes}
              />
            </div>
          )
        } else {
          if (buttonRowAlignment === 'left') {
            buttonCSS.push(buttonAlignmentLeft);
          } else if (buttonRowAlignment === 'right') {
            buttonCSS.push(buttonAlignmentRight);
          }

        }

        // If we want to use the IconButton component the label needs to be set to "only_icon" in Contentful because the empty label would be had "Submit" as default value, and not an empty string
        if (buttonText === 'only_icon') {
          return (
            <div css={buttonCSS} className={`button ${button.rank === "tertiary" ? "button--tertiary-wrapper" : ""}`} key={button.id}>
              <IconButton
                className="ctaButton"
                {...customAttributeData}
                htmlAttributes={{ ...htmlAttributes }}
                appearance={appearance}
                inverse={buttonInverse}
                icon={{ name: button.iconRef }}
                onClick={onButtonClick}
              />
            </div>
          );
        } else {
          let icon = undefined;

          if (button.iconRef && button.iconRef !== 'beta_tooltip') {
            icon = { name: button.iconRef, justify: 'left' };
          }

          return (
            <div css={buttonCSS} className={`button ${button.rank === "tertiary" ? "button--tertiary" : ""}`} key={button.id}>
              <Button
                className="ctaButton"
                href={getButtonHref()}
                {...customAttributeData}
                dataAttributes={{ ...dataAttributes }}
                htmlAttributes={{ ...htmlAttributes }}
                appearance={appearance}
                inverse={buttonInverse}
                icon={icon}
                text={buttonText}
                onClick={onButtonClick}

              />
            </div>
          );
        }
      }
    });

    const colNums = getColNums(buttons);

    return (
      // The Tealium SEO solution is heavily used in the project, and JQuery is a dependency of it.
      // JQuery overrides some of the properties of the React ThemeProvider in some OPCOs.
      // Because of this, the ThemeProvider needs to be reinjected in the CtaButtonRow component to reinitalize the overridden properties.
      <ThemeProvider theme={themeWS10}>
        <div css={gridContainer}>
          <SimpleGridWrapper>
            <SimpleGrid
              columns={{
                sm: 1,
                md: colNums,
              }}
              spacing={buttons.length > 1 ? spacing : 0}
              justify={
                buttonRowAlignment === 'centre' || !buttonRowAlignment
                  ? 'center'
                  : buttonRowAlignment
              }
              verticalAlign="center"
            >
              {renderedButtons}
            </SimpleGrid>
          </SimpleGridWrapper>
        </div>
      </ThemeProvider>
    );
  }
  // No CTA buttons, return nothing.
  return null;
};

export default CtaButtonRow;
